import AlertModal from "@/components/shared/AlertModal";

const DOCUMENT_TYPE_LABELS = {
  resume: "a resume",
  aadhaar_card: "an Aadhaar card",
  pan_card: "a PAN card",
  passport: "a passport",
  driving_license: "a driving license",
  certificate: "a certificate",
  image: "an image",
  other: "an unrelated document",
};

const buildDescription = (validation) => {
  if (!validation) return "";

  const { is_resume: isResume, document_type: documentType, detected_role: detectedRole, job_title: jobTitle, reason } = validation;

  if (isResume === false) {
    const label = DOCUMENT_TYPE_LABELS[documentType] || "not a resume";
    return `This file looks like ${label}, not a resume. ${reason || ""}`.trim();
  }

  if (detectedRole && jobTitle) {
    return `This job (${jobTitle}) may not match the candidate's profile (${detectedRole}). ${reason || ""}`.trim();
  }

  return reason || "This resume may not be relevant to the selected job.";
};

/**
 * Shown when the Resume Validation API flags an uploaded file — either it
 * isn't a resume at all, or it doesn't look relevant to the selected job.
 * Never hard-blocks: both actions let the recruiter/candidate proceed one
 * way or another. Built on the existing AlertModal primitive rather than a
 * new dialog component.
 *
 * @param {"individual"|"bulk"} mode - "individual" shows Go Back; "bulk"
 *   shows Ignore (batches with more than one item). Single-item bulk batches
 *   should pass "individual" so Go Back is shown instead of Ignore.
 * @param {string|null} error - set when the caller's chosen action (Ignore /
 *   Go Back / Continue Anyway) failed server-side. Kept open with this
 *   message rather than closing silently, so the recruiter can see what
 *   happened and retry instead of the resume being left stuck with no way
 *   back to this popup.
 */
const ResumeValidationModal = ({
  open,
  fileName,
  validation,
  mode = "individual",
  loading = false,
  error = null,
  onGoBack = () => {},
  onIgnore = () => {},
  onContinueAnyway = () => {},
}) => {
  const isBulk = mode === "bulk";

  return (
    <AlertModal
      open={open}
      title={`Please review: ${fileName || "this resume"}`}
      description={
        <>
          {buildDescription(validation)}
          {error && (
            <>
              <br />
              <span className="text-tone-rose">{error}</span>
            </>
          )}
        </>
      }
      cancelText={isBulk ? "Ignore" : "Go Back"}
      actionText="Continue Anyway"
      loading={loading}
      onOpenChange={() => {}}
      onCancel={isBulk ? onIgnore : onGoBack}
      onAction={onContinueAnyway}
    />
  );
};

export default ResumeValidationModal;
